import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, HelpCircle, ChevronDown, CreditCard, ArrowLeft, User as UserIcon, Settings, Home as HomeIcon, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import HelpDialog from "@/components/HelpDialog";
import SubscriptionDialog from "@/components/SubscriptionDialog";
import AccountSettings from "@/components/AccountSettings";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Shared header utilities. Beta feedback (Feb 26 2026): "top bar felt
// messy". On mobile we now collapse Home / Dashboard / Coach / How-to
// into a single overflow menu triggered by the profile button — the
// header keeps only Messages + Menu visible so it reads as clean as the
// unified square-CTA sections below. Desktop keeps the inline buttons.
export default function HeaderUtils() {
  const { email, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Hide the Home button when we're already on Home.
  const onHome = location.pathname === "/" || location.pathname === "/home";

  const navItemCls = "text-white focus:bg-white/10 focus:text-white cursor-pointer gap-2 py-2.5";

  return (
    <div className="flex items-center gap-1.5 sm:gap-3">
      {/* Inline nav — desktop only. Mobile shows these inside the profile menu. */}
      {!onHome && (
        <button data-testid="header-home-btn" onClick={() => navigate("/")} title="Back to Home"
          className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E2E30A]/40 text-[#E2E30A] hover:bg-[#E2E30A]/10 transition-colors text-[10px] uppercase tracking-wider">
          <HomeIcon className="h-3.5 w-3.5" /> Home
        </button>
      )}
      {isAdmin && (
        <button data-testid="header-back-dashboard-btn" onClick={() => navigate("/admin")} title="Back to admin dashboard"
          className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E2E30A]/40 text-[#E2E30A] hover:bg-[#E2E30A]/10 transition-colors text-[10px] uppercase tracking-wider">
          <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
        </button>
      )}
      <button data-testid="guides-menu-btn" onClick={() => setHelpOpen(true)} title="How to use this app"
        className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-white/15 text-white hover:bg-white/5 transition-colors text-[10px] uppercase tracking-wider">
        <HelpCircle className="h-3.5 w-3.5" /> How to
      </button>

      {/* Profile menu — swaps to a Menu icon on mobile so the label doesn't
          fight the icons. Holds every mobile-only nav shortcut. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="profile-menu-btn" title="Menu & profile"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-white/15 text-white hover:bg-white/5 transition-colors text-[10px] uppercase tracking-wider">
            <Menu className="h-3.5 w-3.5 lg:hidden" />
            <UserIcon className="h-3.5 w-3.5 hidden lg:inline-flex" />
            <span className="hidden lg:inline">Profile</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#141414] border-white/10 rounded-none text-white w-60">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-zinc-300 truncate">{email}</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />

          {/* Mobile-only nav shortcuts (hidden on sm+). Duplicates the desktop
              inline buttons so mobile users still reach every destination. */}
          {!onHome && (
            <DropdownMenuItem data-testid="menu-home" onClick={() => navigate("/")}
              className={`${navItemCls} lg:hidden`}>
              <HomeIcon className="h-4 w-4 text-[#E2E30A]" /> Home
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}
              className={`${navItemCls} lg:hidden`}>
              <ArrowLeft className="h-4 w-4 text-[#E2E30A]" /> Admin dashboard
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="menu-how-to" onClick={() => setHelpOpen(true)}
            className={`${navItemCls} lg:hidden`}>
            <HelpCircle className="h-4 w-4 text-white" /> How to use the app
          </DropdownMenuItem>
          {(!onHome || isAdmin) && <DropdownMenuSeparator className="bg-white/10 lg:hidden" />}

          <DropdownMenuItem data-testid="menu-account" onClick={() => setSettingsOpen(true)} className={navItemCls}>
            <Settings className="h-4 w-4 text-[#E2E30A]" /> Account settings
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="menu-subscription" onClick={() => setSubOpen(true)} className={navItemCls}>
            <CreditCard className="h-4 w-4 text-[#4C9AFF]" /> Subscription
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem data-testid="logout-btn" onClick={logout}
            className="text-[#FF3B30] focus:bg-[#FF3B30]/10 focus:text-[#FF3B30] cursor-pointer gap-2 py-2.5">
            <LogOut className="h-4 w-4" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      <SubscriptionDialog open={subOpen} onOpenChange={setSubOpen} />
      <AccountSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
